import React, { memo, useCallback, useMemo } from "react";

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";

import colors from "app/colors";
import { useOverlay } from "modules/providers/overlay-provider";
import { IFileInTransit } from "modules/providers/index-db-provider/index-db-models";
import prettyBytes from "pretty-bytes";
import {
  isAudio,
  isOffice,
  isPdf,
  isText,
  isVideo,
} from "modules/utils/Functions";
import FileOfficeViewer from "modules/files/file-office-viewer";
import FilePdfViewer from "modules/files/file-pdf-viewer";
import FileTextViewer from "modules/files/file-text-viewer";
import FileVideoPlayer from "modules/files/file-video-player";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { TrashOutlineIcon } from "modules/icons";
import { useIndexDb } from "modules/providers/index-db-provider";
import PRESIGNED_URL_QUERY from "./networking/presigned-url";
import { useQuery } from "@apollo/client";
import { PresignedUrlQuery, PresignedUrlQueryVariables } from "gql/graphql";
import { OFFICE_BASE_URL } from "modules/utils/Constants";
import FileThumb from "modules/files/file-thumb";
import { FILE_MODAL_CLOSE_BUTTON_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  file: IFileInTransit;
}

const FileInTransitModal = ({ file }: IProps) => {
  const { closeModal } = useOverlay();
  const { db } = useIndexDb();

  const onDeleteFileClicked = useCallback(() => {
    db.file.delete(file.id);
    closeModal();
  }, [db.file, file.id, closeModal]);

  return (
    <Modal isOpen onClose={closeModal} size="full">
      <ModalOverlay />
      <ModalContent h="100vh" bg={colors.white} p={0} m={0}>
        <ModalCloseButton
          color={colors.white}
          zIndex={FILE_MODAL_CLOSE_BUTTON_Z_INDEX}
        />
        <ModalBody m={0} p={4} bg={colors.black}>
          <Stack alignItems="center" h="full">
            <HStack>
              <Text color={colors.white}>{file.name}</Text>
              <Text color={colors.grayMain} fontSize={14}>
                {prettyBytes(file.byteCount, {
                  binary: true,
                })}
              </Text>
            </HStack>
            <FileModalItem file={file} />
            <VStack py={2}>
              <CircularIconButton
                icon={<TrashOutlineIcon fill={colors.white} boxSize={6} />}
                aria-label="delete"
                bg={colors.gray90}
                variant="hover"
                onClick={onDeleteFileClicked}
              />
              <Text color={colors.grayPrimary} fontWeight={500} fontSize={10}>
                Delete
              </Text>
            </VStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const FileModalItem = ({ file }: IProps) => {
  // Images are handled by allowing the user to draw on them rather than by displaying them here
  const { data } = useQuery<PresignedUrlQuery, PresignedUrlQueryVariables>(
    PRESIGNED_URL_QUERY,
    {
      variables: {
        key: file.key,
      },
      skip: !isOffice(file.contentType),
    },
  );

  const fileUrl = useMemo(
    () =>
      data?.presignedUrl
        ? `${OFFICE_BASE_URL}${encodeURIComponent(data?.presignedUrl)}`
        : null,

    [data?.presignedUrl],
  );

  if (isOffice(file.contentType)) {
    return <FileOfficeViewer fileUrl={fileUrl} name={file.name} />;
  }

  if (isPdf(file.contentType)) {
    return <FilePdfViewer fileUrl={file.url} name={file.name} />;
  }

  if (isVideo(file.contentType)) {
    return (
      <FileVideoPlayer isAudio={isAudio(file.contentType)} fileUrl={file.url} />
    );
  }

  if (isText(file.contentType)) {
    return <FileTextViewer fileUrl={file.url} />;
  }

  return (
    <VStack spacing={2} h="full" justifyContent="center">
      <FileThumb
        contentType={file.contentType}
        name={file.name}
        url={file.previewCloudFrontUrl}
        size={[179, 135]}
      />
      <Text
        pt={2}
        fontFamily="Outfit"
        fontSize={24}
        fontWeight={700}
        color={colors.white}
      >
        Preview Unavailable
      </Text>
      <Text fontSize={14} fontWeight={600} color={colors.gray30}>
        This file type cannot be shown as a preview.
      </Text>
    </VStack>
  );
};

export default memo(FileInTransitModal);
